import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import type React from 'react';

export function Fieldset({
  className,
  ...props
}: { className?: string } & Omit<Headless.FieldsetProps, 'as' | 'className'>) {
  return (
    <Headless.Fieldset
      {...props}
      className={clsx(className, 'tw:[&>*+[data-slot=control]]:mt-6 tw:[&>[data-slot=text]]:mt-1')}
    />
  );
}

export function Legend({
  className,
  ...props
}: { className?: string } & Omit<Headless.LegendProps, 'as' | 'className'>) {
  return (
    <Headless.Legend
      data-slot="legend"
      {...props}
      className={clsx(
        className,
        'tw:text-base/6 tw:font-semibold tw:text-zinc-950 tw:data-[disabled]:opacity-50 tw:sm:text-sm/6 tw:dark:text-white'
      )}
    />
  );
}

type FieldGroupProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

export function FieldGroup({ className, ...props }: FieldGroupProps) {
  return (
    <div data-slot="control" {...props} className={clsx(className, 'tw:space-y-8 tw:!mt-4')} />
  );
}

export function Field({
  className,
  ...props
}: { className?: string } & Omit<Headless.FieldProps, 'as' | 'className'>) {
  return (
    <Headless.Field
      {...props}
      className={clsx(
        className,
        'tw:[&>[data-slot=label]+[data-slot=control]]:mt-1',
        'tw:[&>[data-slot=label]+[data-slot=description]]:mt-1',
        'tw:[&>[data-slot=description]+[data-slot=control]]:mt-3',
        'tw:[&>[data-slot=control]+[data-slot=description]]:mt-3',
        'tw:[&>[data-slot=control]+[data-slot=error]]:mt-3',
        'tw:[&>[data-slot=label]]:font-medium'
      )}
    />
  );
}

export function Label({
  className,
  ...props
}: { className?: string } & Omit<Headless.LabelProps, 'as' | 'className'>) {
  return (
    <Headless.Label
      data-slot="label"
      {...props}
      className={clsx(
        className,
        'tw:select-none tw:text-sm/6 tw:text-zinc-600 tw:data-[disabled]:opacity-50 tw:sm:text-sm/6 tw:dark:text-zinc-300'
      )}
    />
  );
}

export function Description({
  className,
  ...props
}: { className?: string } & Omit<Headless.DescriptionProps, 'as' | 'className'>) {
  return (
    <Headless.Description
      data-slot="description"
      {...props}
      className={clsx(
        className,
        'tw:text-base/6 tw:text-zinc-400 tw:data-[disabled]:opacity-50 tw:sm:text-sm/6 tw:dark:text-zinc-400'
      )}
    />
  );
}

export function ErrorMessage({
  className,
  ...props
}: { className?: string } & Omit<Headless.DescriptionProps, 'as' | 'className'>) {
  return (
    <Headless.Description
      data-slot="error"
      {...props}
      className={clsx(
        className,
        'tw:text-base/6 tw:text-stoplight-danger tw:data-[disabled]:opacity-50 tw:sm:text-sm/6 tw:dark:text-stoplight-danger'
      )}
    />
  );
}
